import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { clubInfoService, handleApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Mail, Save, AlertCircle, RefreshCw, Eye, Edit3 } from 'lucide-react';

const EmailConfigForm = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [emailConfig, setEmailConfig] = useState({
    email_subject: '',
    email_content: ''
  });
  const { toast } = useToast();
  const quillRef = useRef();

  // Load current email configuration
  useEffect(() => {
    const loadEmailConfig = async () => {
      try {
        setLoading(true);
        const clubInfo = await clubInfoService.get();
        setEmailConfig({
          email_subject: clubInfo.email_subject || '📄 Nouveau PV disponible - Comité du {{date}}',
          email_content: clubInfo.email_content || `Hello le comité !

J'espère que vous allez tous bien ! 😊

Le procès-verbal de notre réunion du {{date}} est maintenant disponible en pièce jointe.

📋 Résumé de cette séance :

         - {{membres_presents}} membres présents
         - {{points_traites}} points à l'ordre du jour traités
         - {{taches_assignees}} tâches assignées

N'hésitez pas si vous avez des questions ou des remarques ! 💬

À bientôt 🎾
{{club_name}}`
        });
      } catch (error) {
        console.error('Error loading email configuration:', error);
        // Set default values on error
        setEmailConfig({
          email_subject: '📄 Nouveau PV disponible - Comité du {{date}}',
          email_content: `Hello le comité !

J'espère que vous allez tous bien ! 😊

Le procès-verbal de notre réunion du {{date}} est maintenant disponible en pièce jointe.

📋 Résumé de cette séance :

         - {{membres_presents}} membres présents
         - {{points_traites}} points à l'ordre du jour traités
         - {{taches_assignees}} tâches assignées

N'hésitez pas si vous avez des questions ou des remarques ! 💬

À bientôt 🎾
{{club_name}}`
        });
      } finally {
        setLoading(false);
      }
    };

    loadEmailConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await clubInfoService.update(emailConfig);
      
      toast({
        title: "Configuration sauvegardée",
        description: "Les paramètres d'email ont été mis à jour avec succès.",
      });
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de sauvegarder la configuration : ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEmailConfig({
      email_subject: '📄 Nouveau PV disponible - Comité du {{date}}',
      email_content: `Hello le comité !

J'espère que vous allez tous bien ! 😊

Le procès-verbal de notre réunion du {{date}} est maintenant disponible en pièce jointe.

📋 Résumé de cette séance :

         - {{membres_presents}} membres présents
         - {{points_traites}} points à l'ordre du jour traités
         - {{taches_assignees}} tâches assignées

N'hésitez pas si vous avez des questions ou des remarques ! 💬

À bientôt 🎾
{{club_name}}`
    });
  };

  // Custom editor functions
  const editorRef = useRef();

  // Fonction simple pour insérer les variables dans le textarea
  const insertSimpleVariable = (variable) => {
    const editor = editorRef.current;
    if (editor) {
      const start = editor.selectionStart;
      const end = editor.selectionEnd;
      const currentValue = editor.value;
      
      const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
      
      setEmailConfig(prev => ({ 
        ...prev, 
        email_content: newValue 
      }));
      
      // Remettre le focus et la position du curseur
      setTimeout(() => {
        editor.focus();
        editor.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  // Fonction pour insérer du formatage HTML
  const insertFormatting = (startTag, endTag = '') => {
    const editor = editorRef.current;
    if (editor) {
      const start = editor.selectionStart;
      const end = editor.selectionEnd;
      const currentValue = editor.value;
      const selectedText = currentValue.slice(start, end);
      
      let insertText;
      if (selectedText) {
        // Si du texte est sélectionné, l'entourer avec les balises
        insertText = startTag + selectedText + (endTag || startTag.replace('<', '</'));
      } else {
        // Si pas de sélection, insérer les balises vides
        insertText = startTag + (endTag || startTag.replace('<', '</'));
      }
      
      const newValue = currentValue.slice(0, start) + insertText + currentValue.slice(end);
      
      setEmailConfig(prev => ({ 
        ...prev, 
        email_content: newValue 
      }));
      
      // Remettre le focus et positionner le curseur
      setTimeout(() => {
        editor.focus();
        if (selectedText) {
          editor.setSelectionRange(start + insertText.length, start + insertText.length);
        } else {
          // Positionner le curseur entre les balises
          const cursorPos = start + startTag.length;
          editor.setSelectionRange(cursorPos, cursorPos);
        }
      }, 0);
    }
  };

  // Fonction pour insérer une liste
  const insertList = (type) => {
    const editor = editorRef.current;
    if (editor) {
      const start = editor.selectionStart;
      const end = editor.selectionEnd;
      const currentValue = editor.value;
      const selectedText = currentValue.slice(start, end);
      
      let listItems;
      if (selectedText) {
        // Si du texte est sélectionné, le transformer en liste
        const lines = selectedText.split('\n').filter(line => line.trim());
        if (type === 'numbered') {
          listItems = lines.map((line, index) => `${index + 1}. ${line.trim()}`).join('\n');
        } else {
          listItems = lines.map(line => `• ${line.trim()}`).join('\n');
        }
      } else {
        // Si pas de sélection, insérer un exemple de liste
        if (type === 'numbered') {
          listItems = '1. Premier élément\n2. Deuxième élément\n3. Troisième élément';
        } else {
          listItems = '• Premier élément\n• Deuxième élément\n• Troisième élément';
        }
      }
      
      const newValue = currentValue.slice(0, start) + listItems + currentValue.slice(end);
      
      setEmailConfig(prev => ({ 
        ...prev, 
        email_content: newValue 
      }));
      
      // Remettre le focus
      setTimeout(() => {
        editor.focus();
        editor.setSelectionRange(start + listItems.length, start + listItems.length);
      }, 0);
    }
  };

  // Fonction pour générer la prévisualisation avec formatage
  const getPreviewContent = () => {
    const sampleData = {
      '{{date}}': '<strong>17 janvier 2025</strong>',
      '{{club_name}}': '<span style="font-weight: bold; font-size: inherit;">Tennis Club Cheyres-Châbles</span>',
      '{{membres_presents}}': '<strong>4</strong>',
      '{{points_traites}}': '<strong>6</strong>', 
      '{{taches_assignees}}': '<strong>3</strong>'
    };

    let previewContent = emailConfig.email_content || '';
    
    // Remplacer les variables par des exemples avec formatage
    Object.entries(sampleData).forEach(([variable, value]) => {
      previewContent = previewContent.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    // Traitement plus naturel des sauts de ligne et formatage HTML
    previewContent = previewContent
      // Formater le titre "📋 Résumé de cette séance :" avant le traitement des paragraphes
      .replace(/📋 Résumé de cette séance :/g, '<strong style="font-size: 18px; color: #065f46;">📋 Résumé de cette séance :</strong>')
      // Diviser en paragraphes basés sur les doubles sauts de ligne
      .split('\n\n')
      .map(paragraph => {
        if (!paragraph.trim()) return '';
        
        // Traitement spécial pour les lignes avec tirets (listes)
        if (paragraph.includes('         -') || paragraph.includes('• ') || /^\d+\. /.test(paragraph)) {
          const lines = paragraph.split('\n').map(line => {
            // Listes avec tirets (format original)
            if (line.includes('         -')) {
              return line.replace(/         - (.+)/g, '<div style="margin-left: 20px;">• $1</div>');
            }
            // Listes à puces
            else if (line.trim().startsWith('• ')) {
              return `<div style="margin-left: 20px;">${line.trim()}</div>`;
            }
            // Listes numérotées
            else if (/^\d+\. /.test(line.trim())) {
              return `<div style="margin-left: 20px;">${line.trim()}</div>`;
            }
            return line;
          });
          return lines.join('<br>');
        }
        
        // Paragraphe normal : remplacer les sauts de ligne simples par <br>
        return paragraph.replace(/\n/g, '<br>');
      })
      .filter(p => p) // Enlever les paragraphes vides
      .map(p => `<div style="margin-bottom: 12px;">${p}</div>`) // Espacement naturel entre paragraphes
      .join('');

    return previewContent;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
          <p className="text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="w-5 h-5 text-blue-600" />
          Configuration des emails
        </CardTitle>
        <CardDescription className="text-sm">
          Personnalisez le contenu des emails envoyés aux membres
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="email_subject" className="text-sm font-medium">
              Sujet de l'email
            </Label>
            <Input
              id="email_subject"
              value={emailConfig.email_subject}
              onChange={(e) => setEmailConfig(prev => ({ ...prev, email_subject: e.target.value }))}
              placeholder="Sujet de l'email..."
              className="border-blue-200 focus:border-blue-400"
            />
            <p className="text-xs text-gray-500">
              Variables disponibles : {'{date}'}, {'{club_name}'}
            </p>
          </div>

          {/* Content Editor Toggle */}
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              Contenu de l'email
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={!showPreview ? "default" : "outline"}
                size="sm"
                onClick={() => setShowPreview(false)}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Éditer
              </Button>
              <Button
                type="button"
                variant={showPreview ? "default" : "outline"}
                size="sm"
                onClick={() => setShowPreview(true)}
              >
                <Eye className="w-4 h-4 mr-2" />
                Aperçu
              </Button>
            </div>
          </div>

          {/* Content Editor */}
          {!showPreview ? (
            <div className="space-y-4">
              {/* Barre d'outils de formatage */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-700 mb-2">✏️ Outils de formatage :</h4>
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Formatage de base */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertFormatting('<strong>', '</strong>')}
                    className="text-xs bg-gray-50 hover:bg-gray-100 px-2 py-1"
                    title="Gras"
                  >
                    <strong>B</strong>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm" 
                    onClick={() => insertFormatting('<em>', '</em>')}
                    className="text-xs bg-gray-50 hover:bg-gray-100 px-2 py-1"
                    title="Italique"
                  >
                    <em>I</em>
                  </Button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* Police de caractères */}
                  <select 
                    onChange={(e) => {
                      if (e.target.value) {
                        insertFormatting(`<span style="font-family: ${e.target.value};">`, '</span>');
                        e.target.value = ''; // Reset
                      }
                    }}
                    className="text-xs border rounded px-2 py-1 bg-white"
                    title="Police de caractères"
                  >
                    <option value="">Police</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Tahoma, sans-serif">Tahoma</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                  </select>

                  {/* Taille de police */}
                  <select 
                    onChange={(e) => {
                      if (e.target.value) {
                        insertFormatting(`<span style="font-size: ${e.target.value};">`, '</span>');
                        e.target.value = ''; // Reset
                      }
                    }}
                    className="text-xs border rounded px-2 py-1 bg-white"
                    title="Taille de police"
                  >
                    <option value="">Taille</option>
                    <option value="12px">Petit (12px)</option>
                    <option value="14px">Normal (14px)</option>
                    <option value="16px">Moyen (16px)</option>
                    <option value="18px">Grand (18px)</option>
                    <option value="20px">Très grand (20px)</option>
                  </select>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* Listes */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertList('bullet')}
                    className="text-xs bg-gray-50 hover:bg-gray-100 px-2 py-1"
                    title="Liste à puces"
                  >
                    • Liste
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertList('numbered')}
                    className="text-xs bg-gray-50 hover:bg-gray-100 px-2 py-1"
                    title="Liste numérotée"
                  >
                    1. Liste
                  </Button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* Couleur du texte */}
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">Couleur:</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => insertFormatting('<span style="color: #000000;">', '</span>')}
                        className="w-5 h-5 rounded border border-gray-300"
                        style={{backgroundColor: '#000000'}}
                        title="Noir"
                      />
                      <button
                        type="button"
                        onClick={() => insertFormatting('<span style="color: #065f46;">', '</span>')}
                        className="w-5 h-5 rounded border border-gray-300"
                        style={{backgroundColor: '#065f46'}}
                        title="Vert foncé"
                      />
                      <button
                        type="button"
                        onClick={() => insertFormatting('<span style="color: #dc2626;">', '</span>')}
                        className="w-5 h-5 rounded border border-gray-300"
                        style={{backgroundColor: '#dc2626'}}
                        title="Rouge"
                      />
                      <button
                        type="button"
                        onClick={() => insertFormatting('<span style="color: #2563eb;">', '</span>')}
                        className="w-5 h-5 rounded border border-gray-300"
                        style={{backgroundColor: '#2563eb'}}
                        title="Bleu"
                      />
                      <button
                        type="button"
                        onClick={() => insertFormatting('<span style="color: #7c3aed;">', '</span>')}
                        className="w-5 h-5 rounded border border-gray-300"
                        style={{backgroundColor: '#7c3aed'}}
                        title="Violet"
                      />
                      <button
                        type="button"
                        onClick={() => insertFormatting('<span style="color: #ea580c;">', '</span>')}
                        className="w-5 h-5 rounded border border-gray-300"
                        style={{backgroundColor: '#ea580c'}}
                        title="Orange"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Sélectionnez du texte et cliquez sur un bouton pour le formater, ou cliquez sans sélection pour insérer le formatage.
                </p>
              </div>

              {/* Boutons Variables */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-700 mb-2">🔧 Variables de template :</h4>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertSimpleVariable('{{date}}')}
                    className="text-xs bg-blue-50 hover:bg-blue-100"
                  >
                    📅 {'{date}'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertSimpleVariable('{{club_name}}')}
                    className="text-xs bg-blue-50 hover:bg-blue-100"
                  >
                    🏛️ {'{club_name}'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertSimpleVariable('{{membres_presents}}')}
                    className="text-xs bg-blue-50 hover:bg-blue-100"
                  >
                    👥 {'{présents}'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertSimpleVariable('{{points_traites}}')}
                    className="text-xs bg-blue-50 hover:bg-blue-100"
                  >
                    📋 {'{points}'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertSimpleVariable('{{taches_assignees}}')}
                    className="text-xs bg-blue-50 hover:bg-blue-100"
                  >
                    ✅ {'{tâches}'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertSimpleVariable('\n📋 Résumé de cette séance :\n')}
                    className="text-xs bg-green-50 hover:bg-green-100"
                  >
                    🎨 Titre section
                  </Button>
                </div>
              </div>

              {/* Éditeur Simple avec Textarea */}
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <div className="border-b border-gray-200 p-2 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    ✏️ <strong>Éditeur simple</strong> - Tapez votre contenu directement. Utilisez les boutons ci-dessus pour insérer des variables.
                  </p>
                </div>
                
                <textarea
                  ref={editorRef}
                  value={emailConfig.email_content}
                  onChange={(e) => {
                    setEmailConfig(prev => ({ 
                      ...prev, 
                      email_content: e.target.value 
                    }));
                  }}
                  className="w-full p-4 min-h-[300px] max-h-[500px] resize-none focus:outline-none border-none"
                  style={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#333',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                  placeholder="Tapez le contenu de votre email ici..."
                />
              </div>
            </div>
          ) : (
            /* Preview */
            <div className="space-y-2">
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[200px]" style={{direction: 'ltr', textAlign: 'left'}}>
                <div 
                  className="prose prose-sm max-w-none"
                  style={{direction: 'ltr', textAlign: 'left'}}
                  dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                />
              </div>
              <p className="text-xs text-gray-500">
                Aperçu avec exemples de données (les vraies valeurs seront utilisées lors de l'envoi)
              </p>
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800">
                <p className="font-medium mb-1">💡 À propos des emails</p>
                <p>Ces paramètres seront utilisés lorsque vous cliquez sur "EMAIL" à côté d'un PV pour envoyer une notification aux membres du comité.</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button 
              type="submit" 
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </Button>
            
            <Button 
              type="button" 
              variant="outline"
              onClick={handleReset}
              disabled={saving}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default EmailConfigForm;